import type { BucketSerie, StatusAlvo } from '../../../api/monitoramento';

/**
 * Mini sparkline 24h × 1h (24 barras) renderizada em SVG inline. Cada barra
 * representa o status dominante daquele bucket horário; sem dados →
 * barra cinza fina.
 *
 * <p>Compacta o suficiente para caber no card do header (~110×24px).
 * Diferente de UptimeBars (que é 7d × 1h = 168 barras com tooltip rico),
 * aqui priorizamos densidade visual sem interatividade — para drill ir
 * direto pro detalhe do alvo (Sheet) que tem o UptimeBars completo.
 */
export interface SparklineMiniProps {
  buckets: BucketSerie[];
  /** Número de buckets esperado — preenche com vazios à esquerda quando vier menos. */
  totalBarras?: number;
}

export function SparklineMini({ buckets, totalBarras = 24 }: SparklineMiniProps) {
  // Pega só as últimas N barras (ordenadas cronologicamente do servidor).
  const ultimas = buckets.slice(-totalBarras);
  const faltam = Math.max(0, totalBarras - ultimas.length);
  const todas: (BucketSerie | null)[] = [
    ...Array.from({ length: faltam }, () => null),
    ...ultimas,
  ];

  const larguraTotal = totalBarras * 4 + (totalBarras - 1); // 4px barra + 1px gap
  const altura = 18;

  return (
    <svg
      width={larguraTotal}
      height={altura}
      viewBox={`0 0 ${larguraTotal} ${altura}`}
      role="img"
      aria-label="Status horário das últimas 24 horas"
      className="overflow-visible"
    >
      {todas.map((b, i) => {
        const x = i * 5;
        const cor = corBucket(b);
        const alturaBarra = b ? altura : Math.max(2, altura * 0.3);
        const y = altura - alturaBarra;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={4}
            height={alturaBarra}
            fill={cor}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

function corBucket(b: BucketSerie | null): string {
  if (!b || b.total === 0) return '#d1d5db'; // gray-300 (SEM_DADOS)
  return corStatus(b.status_dominante);
}

function corStatus(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return '#22c55e'; // green-500
    case 'DEGRADADO':
      return '#eab308'; // yellow-500
    case 'INDISPONIVEL':
      return '#ef4444'; // red-500
    case 'SEM_DADOS':
      return '#d1d5db'; // gray-300
  }
}
